import React, { forwardRef, useImperativeHandle, useState, useEffect, useCallback } from "react";
import { Form, Input, Select, Row, Col, TimePicker, Typography, Button, message, Tooltip, Modal } from "antd";
import dayjs from "dayjs";
import {
  
  updateCrewAndEmployeeDetails,
  getCrewTemplateList,getCrewUser
} from "../../services/crew";
import { getAllEvents } from "../../services/selfOnboarding";
import "./Style.css";
import { fieldAutoSuggest } from "../../services/selfOnboarding";
import useAuth from "../../hooks/useAuth";
import { getDetails } from "../../services/auth";
import {Spin} from "antd";
const { Option } = Select;
const { Title } = Typography;

const BasicDetails = forwardRef(({ onChange, onNext, initialValues, mode, onClose, assignDate, templateAccess, isEmptyCrew, onRefresh }, ref) => {
  const [form] = Form.useForm();
  const [crewTemplates, setCrewTemplates] = useState([]);
  const [crewTemplateMap, setCrewTemplateMap] = useState({});
  const [crewAllData, setCrewAllData] = useState([]);
  const [labourData, setLabourData] = useState({});
  const [startTimeError, setStartTimeError] = useState("");
  const [workmen, setWorkmen] = useState([]);
  const [canUpdate, setCanUpdate] = useState(false);
  const [showOvernightWarning, setShowOvernightWarning] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const showSpecific = (parameter) => (permissions || []).find(
      (f) => f.feature && f.feature.toLowerCase().includes(parameter.toLowerCase())
  );
    const eventAccess = !(showSpecific('Non-mandatory Event and Task')?.access === false);
   const { userConfig } = useAuth();
  const isFieldDisabled = mode === "edit" && !isEmptyCrew;
 useImperativeHandle(ref, () => ({
    validate: () => form.validateFields(),
    resetForm: () => { form.resetFields() },
  }));
const handleValuesChange = (_, allValues) => {
    const filtered = Object.fromEntries(
      Object.entries(allValues).filter(([_, v]) => v !== undefined && v !== null && v !== "")
    );

    const watched = ["venue", "employeesNeeded", "supervisor", "crewName", "crewLead", "startTime", "endTime", "task", "premium", "payScale", "workRuleTransfer", "eventName"];
    const hasChanged = watched.some((k) => {
      const prev = initialValues?.[k];
      const curr = allValues?.[k];
      
      if (k === "startTime" || k === "endTime") {
        const prevTime = dayjs.isDayjs(prev) ? prev.format('HH:mm') : (typeof prev === 'string' ? prev : '');
        const currTime = dayjs.isDayjs(curr) ? curr.format('HH:mm') : (typeof curr === 'string' ? curr : '');
        return prevTime !== currTime;
      }
      const p = (prev === undefined || prev === null || prev === "") ? "" : String(prev).toString().trim();
      const c = (curr === undefined || curr === null || curr === "") ? "" : String(curr).toString().trim();
      
      if (k === "employeesNeeded") {
        return Number(p) !== Number(c);
      }
      return p !== c;
    });
    setCanUpdate(Boolean(hasChanged));

    onChange?.(filtered);
  };

  useEffect(() => {
      getRolePermissionDetails();
    },[])
   const getRolePermissionDetails = () => {
      getDetails(userConfig.orgId)
        .then((response) => {
          const fetchRole = response.data.find((role) => 
            role?.selectedRole?.toLowerCase().trim() === userConfig.role?.toLowerCase().trim() && 
            role?.selectedRoleType?.toLowerCase().trim() === userConfig.roleName?.selectedRoleType?.toLowerCase().trim()
          );
          const crewPermissions = fetchRole.permissions.cap.crewScheduling;
          setPermissions(crewPermissions);
   })
        .catch((err) => {
          console.error(err);
        })
    }
const isOvernightShift = () => {
    const startTime = form.getFieldValue('startTime');
    const endTime = form.getFieldValue('endTime');
    if (!startTime || !endTime) return false;
    return dayjs(endTime).isBefore(dayjs(startTime));
  };
 const handleButtonClick = async () => {
    if (isOvernightShift()) {
      setShowOvernightWarning(true);
      setPendingAction(mode === "edit" ? "save" : "next");
    } else {
      if (mode === "edit") {
        await handleSave();
      } else {
        onNext();
      }
    }
  };
// Handle overnight warning confirmation
const handleOvernightConfirm = async () => {
    setShowOvernightWarning(false);
    if (pendingAction === "save") {
      await handleSave();
    } else if (pendingAction === "next") {
      onNext();
    }
    setPendingAction(null);
  };

  // Handle overnight warning cancellation
  const handleOvernightCancel = () => {
    setShowOvernightWarning(false);
    setPendingAction(null);
  };
// Save changes for edit mode
  const handleSave = async () => {
    const id = initialValues._id?.$oid || initialValues._id || initialValues.id;
    if (!id) return console.error("No crew ID found for update.");
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      const normalizeValue = (val) => val === undefined ? "" : val;
       const payload = {
        crews: [
          {
            crewId: initialValues?._id,
            crewDetailsUpdates: {
              crewName: values?.crewName,
              crewLead: values?.crewLead,
              supervisor: normalizeValue(values?.supervisor),
              crewTemplateName: values?.crewTemplateName,
              employeesNeeded: values?.employeesNeeded,
              venue: normalizeValue(values?.venue),
              task: values?.task,
              taskDescription: normalizeValue(values?.taskDescription),
              premium: normalizeValue(values?.premium),
              premiumDescription: normalizeValue(values?.premiumDescription),
              payScale: normalizeValue(values?.payScale),
              payscaleDescription: normalizeValue(values?.payscaleDescription),
              workRuleTransfer: normalizeValue(values?.workRuleTransfer),
              startTime: values?.startTime ? (dayjs.isDayjs(values.startTime) ? values.startTime.format('HH:mm') : values.startTime) : undefined,
              endTime: values?.endTime ? (dayjs.isDayjs(values.endTime) ? values.endTime.format('HH:mm') : values.endTime) : undefined,
              eventName: values?.eventName,
              eventDescription: normalizeValue(values?.eventDescription),
            },
          },
        ],
      };
   await updateCrewAndEmployeeDetails(payload);
      message.success("Crew details updated successfully");
      if (onClose) {
        onClose();
      }
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      if (err.errorFields && err.errorFields.length > 0) {
        message.error(err.errorFields[0].errors[0]);
      } else {
        console.error("Error updating crew:", err);
        message.error("Failed to update crew details");
      }
    }
  };

  useEffect(() => {
    fetchCrewAll();
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await getAllEvents();
      setLabourData((prev) => ({ ...prev, events: res.data?.events || [] }));
    } catch (err) {
      console.error("Error fetching events:", err);
    }
  };
const fetchCrewAll = async () => {
    try {
      const res = await getCrewTemplateList();
      const crews = res?.data?.templates || [];
      setCrewAllData(crews);
      const map = {};
      const list = [];
      crews.forEach((c) => {
        const key = c?.crewTemplateName || c?.crewTemplate;
        if (key && !map[key]) {
          map[key] = c;
          list.push({ value: key, label: c?.crewTemplateName || key });
        }
      });
      const sortedTemplates = [...list].sort((a, b) =>
        String(a?.label || a?.value || '').localeCompare(String(b?.label || b?.value || ''), undefined, {
          numeric: true,
          sensitivity: 'base',
        })
      );
      setCrewTemplates(sortedTemplates);
      setCrewTemplateMap(map);
    } catch (err) {
      console.error("Error fetching crew data:", err);
    }
  };
useEffect(() => {
    fetchEmployeeList();
  }, []);
 useEffect(() => {
    setCanUpdate(false);
  }, [initialValues]);
  const fetchEmployeeList = async () => {
      try {
        const response = await getCrewUser();
          const incoming = Array.isArray(response?.data) ? response.data : [];
          const uniqueByUserId = new Map();

          incoming.forEach((emp) => {
            const userId = String(emp?.userid || '').trim();
            if (!userId || uniqueByUserId.has(userId)) return;
            uniqueByUserId.set(userId, emp);
          });
            const normalized = [...uniqueByUserId.values()].sort((a, b) => {
            const aLabel = `${a?.userid || ''} - ${a?.lastName || ''}, ${a?.firstName || ''}`;
            const bLabel = `${b?.userid || ''} - ${b?.lastName || ''}, ${b?.firstName || ''}`;
            return aLabel.localeCompare(bLabel, undefined, { numeric: true, sensitivity: 'base' });
          });
        setWorkmen(normalized);
          } catch (error) {
          console.error('Error fetching workmen:', error);
        } finally {
          // setLoading(false);
        }
      };
const handleCrewTemplateChange = (value) => {
  const template = crewTemplateMap[value];
  if (!template) return;
 const parseTime = (t) => {
    if (!t) return undefined;
    if (dayjs.isDayjs(t)) return t;
   const formats = ["HH:mm", "HH:mm:ss", "hh:mm A", "hh:mm a"];
   for (const f of formats) {
      const d = dayjs(t, f, true);
      if (d.isValid()) return d;
    }
    const loose = dayjs(t);
    return loose.isValid() ? loose : undefined;
  };

  const fields = {
    ...template,
    crewName: template.crewTemplateName,
    crewLead: template.crewLead,
    supervisor: template.supervisor,
    task: template.task,
    premium: template.premium,
    payScale:template.payScale,
    workRuleTransfer: template.workRuleTransfer,
    startTime: parseTime(template.startTime),
    endTime: parseTime(template.endTime),
    venue: template.venue,
    eventName: template.eventName,
  };  
    form.setFieldsValue(fields);
    form.setFieldsValue({ crewLeadId: fields.crewLead });
    handleValuesChange(null, fields);
};

const checkDuplicateStartTime = (startTime) => {
  const crewName = form.getFieldValue("crewName");
  const formattedTime = startTime ? startTime.format("HH:mm") : "";
  if (!crewName || !formattedTime) {
    setStartTimeError("");
    return;
  }
const duplicate = crewAllData.some((c) =>
    c.crewName === crewName &&
    (
      c.employeesToAssign?.some(emp => emp.scheduleIn === formattedTime) || 
      c.startTime === formattedTime                                      
    )
  );
setStartTimeError(
    duplicate ? "Duplicate crew detected. Change name or start time." : ""
  );
};
 const makeOptions = (arr = []) =>
    arr.map((item) => {
      const name = item.name || "";
      const desc = item.description || "";
      return {
        value: name,                
        name,                       
        description: desc,          
        searchText: (name + " " + desc).toLowerCase(),
        label: (
          <Tooltip title={`${name}${desc ? " - " + desc : ""}`} overlayStyle={{ pointerEvents: "none" }} placement="right" autoAdjustOverflow>
            <div className="opt-space" style={{ display: "flex", flexDirection: "column" }}>
              <div className="opt-name" style={{ fontWeight: 600 }}>{name}</div>
              {desc ? <div className="opt-desc">{desc}</div> : null}
            </div>
          </Tooltip>
        ),
      };
    });
  const [filteredTaskOptions, setFilteredTaskOptions] = useState([]);
  const [filteredPremiumOptions, setFilteredPremiumOptions] = useState([]);
  const [filteredPayScaleOptions, setFilteredPayScaleOptions] = useState([]);
  const [filteredWorkRuleOptions, setFilteredWorkRuleOptions] = useState([]);
  const [filteredEventOptions, setFilteredEventOptions] = useState([]);
  const [taskOpen, setTaskOpen] = useState(false);
  const [premiumOpen, setPremiumOpen] = useState(false);
  const [payScaleOpen, setPayScaleOpen] = useState(false);
  const [workRuleOpen, setWorkRuleOpen] = useState(false);
  const [eventOpen, setEventOpen] = useState(false);
  const [startTimeOpen, setStartTimeOpen] = useState(false);
  const [endTimeOpen, setEndTimeOpen] = useState(false);
const handleTimeInput = (e, fieldName, setOpenFn) => {
    const input = e.target;
    const key = e.key;
    
    if (!/^[0-9]$/.test(key)) return;
    
    e.preventDefault(); 
    let currentValue = input.value.replace(/[^0-9]/g, ''); 
    const cursorPos = input.selectionStart;
    if (!currentValue || input.selectionStart === 0 && input.selectionEnd === input.value.length) {
      currentValue = '';
    }
    const digit = key;
    // Case 1: First digit is 3-9 -> auto-format to "0X:"
    if (currentValue.length === 0 && /^[3-9]$/.test(digit)) {
      const formatted = `0${digit}:`;
      input.value = formatted;
      setTimeout(() => input.setSelectionRange(3, 3), 0);
      return;
    }
    // Case 2: First digit is 0, 1, or 2 -> allow second digit
    if (currentValue.length === 0 && /^[0-2]$/.test(digit)) {
      input.value = digit;
      return;
    }
    // Case 3: Second digit after 0 or 1 -> auto-add colon
    if (currentValue.length === 1 && /^[0-1]$/.test(currentValue)) {
      const formatted = `${currentValue}${digit}:`;
      input.value = formatted;
      setTimeout(() => input.setSelectionRange(3, 3), 0);
      return;
    }
    // Case 4: Second digit after 2 -> cap at 23 if 4-9, or allow 0-3
    if (currentValue.length === 1 && currentValue === '2') {
      if (/^[4-9]$/.test(digit)) {
        // Cap at 23
        input.value = '23:';
        setTimeout(() => input.setSelectionRange(3, 3), 0);
      } else if (/^[0-3]$/.test(digit)) {
        const formatted = `2${digit}:`;
        input.value = formatted;
        setTimeout(() => input.setSelectionRange(3, 3), 0);
      }
      return;
    }
    // Handle minute input (after colon)
    if (currentValue.length >= 2 && input.value.includes(':')) {
      const parts = input.value.split(':');
      const hourPart = parts[0];
      let minutePart = (parts[1] || '').replace(/[^0-9]/g, '');
      // First minute digit: if 6-9, show error (invalid)
      if (minutePart.length === 0 && /^[6-9]$/.test(digit)) {
        message.error({ content: "Invalid Entry: Minutes cannot exceed 59", key: "invalid-time", duration: 1.5 });
        return;
      }
      // First minute digit: 0-5
      if (minutePart.length === 0 && /^[0-5]$/.test(digit)) {
        input.value = `${hourPart}:${digit}`;
        return;
      }
      // Second minute digit - validate total minutes
      if (minutePart.length === 1) {
        const firstMinuteDigit = parseInt(minutePart[0]);
        const secondMinuteDigit = parseInt(digit);
        const totalMinutes = firstMinuteDigit * 10 + secondMinuteDigit;
        // Check if minutes exceed 59
        if (totalMinutes > 59) {
          message.error({ content: "Invalid Entry: Minutes cannot exceed 59", key: "invalid-time", duration: 1.5 });
          return;
        }
        minutePart += digit;
        const timeValue = dayjs(`${hourPart}:${minutePart}`, 'HH:mm');
        if (timeValue.isValid()) {
          const currentValue = form.getFieldValue(fieldName);
          const isSameTime = currentValue && dayjs(currentValue).format('HH:mm') === timeValue.format('HH:mm');
          setOpenFn(false);
          setTimeout(() => {
            if (!isSameTime) {
              form.setFieldsValue({ [fieldName]: timeValue });
              handleValuesChange({ [fieldName]: timeValue }, { ...form.getFieldsValue(), [fieldName]: timeValue });
            }
            input.value = `${hourPart}:${minutePart}`;
          }, 10);
        }
        return;
      }
    }
  };
const debounceTasks = useCallback(() => {
  let timer;
return (searchText, setterFn, parameter, stateKey, done) => {
if (timer) clearTimeout(timer);
if (!searchText) {
      setterFn([]);
      setLoading(false);
      if (done) done([]);
      return;
    }
setLoading(true); // ✅ start loading immediately
timer = setTimeout(async () => {
try {
 const { data } = await fieldAutoSuggest({
          parameter,
          searchText
        });

        const arr = data?.[stateKey] || [];
        const formatted = makeOptions(arr);
         setterFn(formatted);
         if (done) done(formatted);
          } catch (error) {
        console.error(error);
        setterFn([]);
         if (done) done([]);
        } finally {
        setLoading(false); // ✅ IMPORTANT FIX
         }
       }, 1000);
};
}, []);
// Create debounced handlers for each field
  const debouncedSearch = useCallback(debounceTasks(), [debounceTasks]);
  const handleTaskSearch = (searchText, done) => debouncedSearch(searchText, setFilteredTaskOptions, "task_sub_task", "task_sub_task", done);
  const handlePremiumSearch = (searchText, done) => debouncedSearch(searchText, setFilteredPremiumOptions, "level", "level", done);
  const handlePayScaleSearch = (searchText, done) => debouncedSearch(searchText, setFilteredPayScaleOptions, "payscale_group", "payscale_group", done);
  const handleWorkRuleSearch = (searchText, done) => debouncedSearch(searchText, setFilteredWorkRuleOptions, "workRule", "workRule", done);
  const handleEventSearch = (searchText, done) => debouncedSearch(searchText, setFilteredEventOptions, "wbs_io", "wbs_io", done);
   return (
      <Spin spinning={loading} tip="Loading..." size="large">
   <Form
      className="form-autoComplete"
      layout="vertical"
      form={form}
      initialValues={initialValues}
      onValuesChange={handleValuesChange}
      autoComplete="off"
      onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
    >
      {/* Crew Template */}
      <Row gutter={16}>
        <Col span={5} hidden={mode === "edit"}>
          <Form.Item label="Crew Template" name="crewTemplate">
            <Select
              placeholder="Select Crew Template"
              onChange={handleCrewTemplateChange}
              allowClear
              disabled={mode === "edit"}
            >
              { templateAccess?.access && templateAccess?.view !== "None" && crewTemplates.map((tpl) => (
                <Option key={tpl.value} value={tpl.value}>
                  {tpl.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      {/* Crew & Supervision */}
      <Title level={5}>Crew & Supervision</Title>
      <Row gutter={16}>
        <Col span={5} hidden>
          <Form.Item label="Crew Template Name" name="crewTemplateName">
            <Input  />
          </Form.Item>
        </Col>
        <Col span={5}>
          <Form.Item
            label="Crew Name"
            name="crewName"
            rules={[{ required: true, message: "Crew Name is required" }]}
            normalize={(value) => value?.replace(/^\s+/, "")}
          >
            <Input placeholder="Enter Crew Name" />
          </Form.Item>
        </Col>
        <Col span={5}>
          <Form.Item label="Crew Lead" name="crewLead" rules={[{ required: true, message: 'Please select a Crew Lead' }]}>
           <Select showSearch placeholder="Select Crew Lead" style={{ width: "100%" }} allowClear 
            onChange={(val) => {
              if (!val) {
                form.setFieldsValue({ crewLead: undefined, crewLeadId: undefined });
              } else {
                // val is the userid
                form.setFieldsValue({ crewLeadId: val });
              }
              }}
              
           >
  {workmen?.map((emp) => (
 <Option 
    key={emp?.userid} 
    // value={emp?.userid}
    value={`${emp?.userid} - ${emp?.lastName}, ${emp?.firstName}`}
  >
    {emp?.userid + " - " + emp?.lastName + ", " + emp?.firstName}
  </Option>
))}
</Select>
 </Form.Item>
        </Col>
        <Col span={5}>
          <Form.Item label="Supervisor" name="supervisor" normalize={(value) => value?.replace(/^\s+/, "")}>
            <Input placeholder="Supervisor"/>
          </Form.Item>
        </Col>
      </Row>
{/* Work Details */}
      <Title level={5}>Work Details</Title>
      <Row gutter={16}>
        <Col span={5}>
          <Form.Item label="Task" name="task"
             rules={
    eventAccess
      ? []   
      : [{ required: true, message: "Please select a Task" }]
  }
          >
            <Select
              showSearch
              placeholder="Enter Task"
              disabled={isFieldDisabled}
              onInputKeyDown={(e) => { if (e.key === " " && e.target.value.length === 0) e.preventDefault();}}
              dropdownMatchSelectWidth={false}
              dropdownStyle={{ width: 300, fontSize: 14 }}
              notFoundContent={filteredTaskOptions.length === 0 && form.getFieldValue("task") !== undefined ? "No data" : null}
              open={taskOpen}
              showArrow ={false}
              onDropdownVisibleChange={(isOpen) => {if (!isOpen || filteredTaskOptions.length < 0) {setFilteredTaskOptions([]); setTaskOpen(false);}}
              }
              onSearch={(text) => {
                handleTaskSearch(text, (results) => {
                  if (results.length === 0 && text.trim().length > 0) {
                    message.error({ content: "No data found", key: "invalid-task",  duration: 1.5});
                  }
                });
                if (text && text.trim().length > 0) setTaskOpen(true);
                else {
                  setTaskOpen(false);
                  setFilteredTaskOptions([]);
                }
              }}
              onBlur={() => {
                const name = form.getFieldValue("task");
                const desc = form.getFieldValue("taskDescription");
                if (!name && !desc){
                form.setFieldsValue({ task: undefined, taskDescription: undefined });
                setTaskOpen(false);}
                setFilteredTaskOptions([]);
              }}
              allowClear
              onChange={(val) => { if (!val) { form.setFieldsValue({ task: undefined, taskDescription: undefined }); setTaskOpen(false); setFilteredTaskOptions([]);} }}
              filterOption={false}
              optionLabelProp="label"
              style={{ width: "100%" }}
              onSelect={(value, option) => {
                const name = option._name;
                const description = option._description || "";
                form.setFieldsValue({ task: name });
                form.setFieldsValue({ taskDescription: description });
                setTaskOpen(false); setFilteredTaskOptions([]);
              }}
            >
              {(filteredTaskOptions || []).map((opt, i) => (
                <Option key={`${opt.value}-${i}`} value={opt.value} _name={opt.name} _description={opt.description} label={opt.name}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="taskDescription" hidden>
         <Input />
       </Form.Item>
        </Col>
        <Col span={5}>
          <Form.Item label="Premium" name="premium">
            <Select
              showSearch
              showArrow ={false}
              placeholder="Enter Premium"
              disabled={isFieldDisabled}
              onInputKeyDown={(e) => { if (e.key === " " && e.target.value.length === 0) e.preventDefault();}}
              dropdownMatchSelectWidth={false}
              dropdownStyle={{ width: 300, fontSize: 14 }}
              notFoundContent={filteredPremiumOptions.length === 0 && form.getFieldValue("premium") !== undefined ? "No data" : null}
              open={premiumOpen}
              onDropdownVisibleChange={(isOpen) => {if (!isOpen || filteredPremiumOptions.length < 0) {setFilteredPremiumOptions([]); setPremiumOpen(false);}}}
              onSearch={(text) => {
                handlePremiumSearch(text, (results) => {
                  if (results.length === 0 && text.trim().length > 0) {
                    message.error({ content: "No data found", key: "invalid-task",  duration: 1.5});
                  }
                });
                if (text && text.trim().length > 0) setPremiumOpen(true);
                else {
                  setPremiumOpen(false);
                  setFilteredPremiumOptions([]);
                }
              }}
              onBlur={() => {
                const name = form.getFieldValue("premium");
                const desc = form.getFieldValue("premiumDescription");
                if (!name && !desc){
                form.setFieldsValue({ premium: undefined, premiumDescription: undefined });
                setPremiumOpen(false);}
                setFilteredPremiumOptions([]);
              }}
              allowClear
              onChange={(val) => { if (!val) { form.setFieldsValue({ premium: undefined, premiumDescription: undefined }); setPremiumOpen(false); setFilteredPremiumOptions([]);} }}
              filterOption={false}
              optionLabelProp="label"
              style={{ width: "100%" }}
              onSelect={(value, option) => {
                const name = option._name;
                const description = option._description || "";
                form.setFieldsValue({ premium: name});
                form.setFieldsValue({ premiumDescription: description });
                setPremiumOpen(false); setFilteredPremiumOptions([]);
              }}
            >
              {(filteredPremiumOptions || []).map((opt, i) => (
                <Option key={`${opt.value}-${i}`} value={opt.value} _name={opt.name} _description={opt.description} label={opt.name}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="premiumDescription" hidden>
         <Input />
       </Form.Item>
   </Col>
         <Col span={5}>
          <Form.Item label="Payscale" name="payScale">
            <Select
              showSearch
              showArrow ={false}
              placeholder="Enter Payscale"
              disabled={isFieldDisabled}
              onInputKeyDown={(e) => { if (e.key === " " && e.target.value.length === 0) e.preventDefault();}}
              notFoundContent={filteredPayScaleOptions.length === 0 && form.getFieldValue("payScale") !== undefined ? "No data" : null}
              onSearch={(text) => {
                handlePayScaleSearch(text, (results) => {
                  if (results.length === 0 && text.trim().length > 0) {
                    message.error({ content: "No data found", key: "invalid-task",  duration: 1.5});
                  }
                });
                if (text && text.trim().length > 0) setPayScaleOpen(true);
                else {
                  setPayScaleOpen(false);
                  setFilteredPayScaleOptions([]);
                }
              }}
              onBlur={() => {
                const name = form.getFieldValue("payScale");
                const desc = form.getFieldValue("payscaleDescription");
                if (!name && !desc){
                form.setFieldsValue({ payScale: undefined, payscaleDescription: undefined });
                setPayScaleOpen(false);}
                setFilteredPayScaleOptions([]);
              }}
              open={payScaleOpen}
              onDropdownVisibleChange={(isOpen) => {if (!isOpen || filteredPayScaleOptions.length < 0) {setFilteredPayScaleOptions([]); setPayScaleOpen(false);}}}
              allowClear
              onChange={(val) => { if (!val) { form.setFieldsValue({ payScale: undefined, payscaleDescription: undefined });setPayScaleOpen(false); setFilteredPayScaleOptions([]); } }}
              filterOption={false}
              optionLabelProp="label"
              style={{ width: "100%" }}
              onSelect={(value, option) => {
                const name = option._name;
                const description = option._description || "";
                form.setFieldsValue({ payScale: name });
                form.setFieldsValue({ payscaleDescription: description });
                setPayScaleOpen(false); setFilteredPayScaleOptions([]);
              }}
              dropdownMatchSelectWidth={false}
              dropdownStyle={{ width: 300, fontSize: 14 }}
            >
               {(filteredPayScaleOptions || []).map((opt, i) => (
                <Option key={`${opt.value}-${i}`} value={opt.value} _name={opt.name} _description={opt.description} label={opt.name}>
                  {opt.label}
                </Option>
              ))}
            </Select>
            
          </Form.Item>
       <Form.Item name="payscaleDescription" hidden>
         <Input />
       </Form.Item>
</Col>
        <Col span={5}>
          <Form.Item label="Work Rule Transfer" name="workRuleTransfer">
            <Select
              showSearch
              showArrow ={false}
              placeholder="Enter Work Rule"
              disabled={isFieldDisabled}
              onInputKeyDown={(e) => { if (e.key === " " && e.target.value.length === 0) e.preventDefault();}}
              dropdownMatchSelectWidth={false}
              dropdownStyle={{ width: 300, fontSize: 14 }}
              notFoundContent={filteredWorkRuleOptions.length === 0 && form.getFieldValue("workRuleTransfer") !== undefined ? "No data" : null}
              onSearch={(text) => {
                handleWorkRuleSearch(text, (results) => {
                  if (results.length === 0 && text.trim().length > 0) {
                    message.error({ content: "No data found", key: "invalid-task",  duration: 1.5});
                  }
                });
                if (text && text.trim().length > 0) setWorkRuleOpen(true);
                else {
                  setWorkRuleOpen(false);
                  setFilteredWorkRuleOptions([]);
                }}}
                onBlur={() => {
                  const name = form.getFieldValue("workRuleTransfer");
                  if (!name){
                  form.setFieldsValue({ workRuleTransfer: undefined });
                  setWorkRuleOpen(false);}
                  setFilteredWorkRuleOptions([]);
                }}
              open={workRuleOpen}
              onDropdownVisibleChange={(isOpen) => {if (!isOpen || filteredWorkRuleOptions.length < 0) {setFilteredWorkRuleOptions([]); setWorkRuleOpen(false);}}}
              allowClear
              onChange={(val) => { if (!val) { form.setFieldsValue({ workRuleTransfer: undefined }); setWorkRuleOpen(false); setFilteredWorkRuleOptions([]); } }}
              filterOption={false}
              optionLabelProp="label"
              style={{ width: "100%" }}
              onSelect={(value, option) => {
                const name = option._name || value;
                form.setFieldsValue({ workRuleTransfer: name });
                setWorkRuleOpen(false); setFilteredWorkRuleOptions([]);
              }}
            >
              {(filteredWorkRuleOptions || []).map((opt, i) => (
                <Option key={`${opt.value}-${i}`} value={opt.value} _name={opt.name} label={opt.name}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item 
            label="No. of Employees" 
            name="employeesNeeded"
            normalize={(value) => value?.replace(/^\s+/, "")}
            // rules={[{ required: true, message: "Minimum value should be 1" }]}
          >
            <Input
              type="number"
              min={1}
              // disabled={mode === "edit"}
              inputMode="numeric"
              onChange={(e) => {
                const v = e.target.value;

                if (v === "" || Number(v) <= 0) {
                  e.target.value = "";                
                }
              }}

              onKeyDown={(e) => {
                const isFirstChar = e.target.value.length === 0;
                if (e.key === "+" || e.key === "e" || e.key === "E" || e.key === ".") {
                  e.preventDefault();                  
                }

                if ( e.key === "-") {
                  message.warning("Value cannot be negative");
                  e.preventDefault();
                }
              }}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Shift Timings */}
      <Title level={5}>Shift Schedule</Title>
      <Row gutter={16}>
        <Col span={5}>
          <Form.Item
            label="Start Time"
            name="startTime"
            rules={[{ required: true, message: "Start Time is required" }]}
            
          >
            <TimePicker
              format="HH:mm"
              style={{ width: "100%" }}
              placeholder="Select time (24hr)"
              onChange={(val) => {
                checkDuplicateStartTime(val);
                form.setFieldsValue({ startTime: val });
                handleValuesChange({ startTime: val }, { ...form.getFieldsValue(), startTime: val });
                if (form.getFieldValue('endTime')) {
                  form.validateFields(['endTime']);
                }
                setStartTimeOpen(false);
              }}
              onKeyDown={(e) => handleTimeInput(e, 'startTime', setStartTimeOpen)}
              disabled={isFieldDisabled}
              needConfirm={false}
              open={startTimeOpen}
              onOpenChange={setStartTimeOpen}
            />
          </Form.Item>
        </Col>
        <Col span={5}>
          <Form.Item
            label="End Time"
            name="endTime"
            rules={[
              { required: true, message: "End Time is required" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const startTime = getFieldValue('startTime');
                  if (!value || !startTime) {
                    return Promise.resolve();
                  }
                  if (dayjs(value).isSame(dayjs(startTime))) {
                    return Promise.reject(new Error('End time cannot be the same as start time'));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
            dependencies={['startTime']}
          >
            <TimePicker
              format="HH:mm"
              style={{ width: "100%" }}
              placeholder="Select time (24hr)"
              disabled={isFieldDisabled}
              needConfirm={false}
              open={endTimeOpen}
              onOpenChange={setEndTimeOpen}
              onKeyDown={(e) => handleTimeInput(e, 'endTime', setEndTimeOpen)}
              onChange={(val) => {
                form.setFieldsValue({ endTime: val });
                handleValuesChange({ endTime: val }, { ...form.getFieldsValue(), endTime: val });
                form.validateFields(['endTime']);
                setEndTimeOpen(false);
              }}
            />
          </Form.Item>
        </Col>
        <Col span={5}>
          <Form.Item label="Venue" name="venue" normalize={(value) => value?.replace(/^\s+/, "")} 
          // rules={[{ required: true, message: "Venue is required" }]}
          >
            <Input placeholder="Enter Venue"/>
          </Form.Item>
        </Col>
        <Col span={5}>
         <Form.Item label="Event Name" name="eventName"
            // rules={[{ required: true, message: "Please select an Event Name" }]} 
            rules={
    eventAccess
      ? []   // ✅ Not mandatory
      : [{ required: true, message: "Please select an Event Name" }] // ✅ Mandatory
  }
    >
          <Select
            showSearch
            showArrow ={false}
            placeholder="Enter Event"
            disabled={isFieldDisabled}
            onInputKeyDown={(e) => { if (e.key === " " && e.target.value.length === 0) e.preventDefault();}}
            className="ultra-compact-auto"
            dropdownMatchSelectWidth={false}
            dropdownStyle={{ width: 300, fontSize: 14 }}
            onBlur={() => {
              const name = form.getFieldValue("eventName");
              const desc = form.getFieldValue("eventDescription");
              if (!name && !desc){
              form.setFieldsValue({ eventDescription: undefined, eventName: undefined });
              setEventOpen(false);}
              setFilteredPremiumOptions([]);
            }}
            onSearch={(text) => {
              if (text.trim().length === 0) {
                form.setFieldsValue({ eventName: undefined });
                setEventOpen(false);
                setFilteredEventOptions([]);
              } else {
                form.setFieldsValue({ eventName: undefined });
                setEventOpen(true);
              }
           handleEventSearch(text, (results) => {
          if (results.length === 0 && text.trim().length > 0) {
                  if(text.trim().length > 0){
                    message.error({
                      content: "No data found",
                      key: "invalid-eventName",
                      duration: 1.5
                    });
                  }
                  return;
                }

                if (results.length > 0) {
                  setEventOpen(true);
                }
              });
            }}
            open={eventOpen}
            onDropdownVisibleChange={(isOpen) => {if (!isOpen || filteredEventOptions.length < 0) {setFilteredEventOptions([]); setEventOpen(false);}}}
            filterOption={false}
            style={{ width: "100%" }}
            allowClear
            onChange={(val) => { if (!val) { form.setFieldsValue({ eventDescription: undefined, eventName: undefined }); setEventOpen(false); setFilteredEventOptions([]);} }}
            optionLabelProp="label"
            onSelect={(value, option) => {
              const name = option._name;
              const description = option._description || "";
              form.setFieldsValue({ eventName: name });
              form.setFieldsValue({ eventDescription: description });
              setEventOpen(false); setFilteredEventOptions([]);
            }}
          >
             {(filteredEventOptions || []).map((opt, i) => (
              <Option key={`${opt.value}-${i}`} value={opt.value} _name={opt.name} _description={opt.description} label={opt.name}>
                {opt.label}
              </Option>
            ))}
          </Select>

</Form.Item>

<Form.Item name="eventDescription" hidden>
  <Input />
</Form.Item>

<Form.Item name="crewLeadId" hidden>
  <Input />
</Form.Item>


        </Col>
      </Row>

      {/* Buttons */}
      <Row justify="end">
        <Col>
          <Button
            type="primary"
            onClick={handleButtonClick}
            style={{ marginTop: 20 }}
            disabled={mode === "edit" && !canUpdate}
          >
            {mode === "edit" ? "Update" : "Next"}
          </Button>
        </Col>
      </Row>

      {/* Overnight Shift Warning Modal */}
      <Modal
        open={showOvernightWarning}
        onCancel={handleOvernightCancel}
        footer={[
          <Button key="no" onClick={handleOvernightCancel}>
            No
          </Button>,
          <Button key="yes" type="primary" onClick={handleOvernightConfirm}>
            Yes
          </Button>,
        ]}
        centered
        closable={false}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: '48px', color: '#faad14', marginBottom: '16px' }}>⚠</div>
          <h2 style={{ marginBottom: '16px' }}>Warning</h2>
          <p style={{ fontSize: '16px' }}>This shift will cross over midnight, continue?</p>
        </div>
      </Modal>
    </Form>
    </Spin>
  )
      
});

export default BasicDetails;
